import React, { useCallback, useEffect, useState } from "react";
import { Picklist, PicklistItem } from "../common/picklist/Picklist";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { setCharity, setCurrentCharityId } from "../../../redux/slices/session";
import { FilledButton } from "../common/buttons/FilledButton";
import { translations } from "../../../utils/translations/translations-manager";
import Loader from "../common/Loader";
import { centsClient } from "../../../api/cents/clients/cents-client";
import { Charity } from "../../../types/charity/charity";
import { useAuth } from "../../../hooks/useAuth";
import { OutlinedButton } from "../common/buttons/OutlinedButton";

function CharitiesPicklist({ values, value, onChange }: { values: Charity[], value: Charity | undefined, onChange: (value: Charity | undefined) => void }): JSX.Element {
  const picks = values.map(value => ({ key: value._id, value }))
  return <Picklist
    picks={picks}
    value={value}
    onChange={(value: PicklistItem<Charity> | undefined) => { onChange(value?.value) }}
    renderer={(value: Charity) => <CharitySelectionItem name={value.name ?? value._id ?? ''} />}
    empty={{ enabled: true, label: translations.texts.select_a_charity.toString() }}
    visibleListClassname="max-h-[16vh]"
  />
}
export function CharitySelector({ onSuccess, skipCharitySelection }: { onSuccess: () => void, skipCharitySelection: (value: boolean) => void }): JSX.Element {
  const { logout } = useAuth()
  const currentCharityId = useSelector((state: RootState) => state.auth.currentCharityId)
  const [charities, setCharities] = useState<Charity[] | undefined>()
  const [error, setError] = useState<'charities_retrieve' | undefined>()
  const [loading, setLoading] = useState<boolean>(false)
  const [selected, setSelected] = React.useState<Charity | undefined>()
  const dispatch: AppDispatch = useDispatch()

  const getCharities = useCallback(async () => {
    try {
      const res = await centsClient.charities.getMyCharities()
      setCharities(res)
    } catch {
      setError('charities_retrieve')
    }
  }, [])

  function checkSingleCharityOnLogin(charities: Charity[]): boolean {
    return !currentCharityId && charities.length === 1
  }

  function loadCharity(charityId: string) {
    setLoading(true)
    void centsClient.charities.getCharity(charityId)
      .then((charity) => {
        dispatch(setCurrentCharityId(charity._id))
        dispatch(setCharity(charity))
        onSuccess()
      })
      .catch(() => {
        logout()
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    getCharities()
  }, [])

  useEffect(() => {
    if (charities) {
      if (checkSingleCharityOnLogin(charities)) {
        skipCharitySelection(true)
        loadCharity(charities[0]._id)
      } else if(charities.length > 0) {
        skipCharitySelection(false)
        setSelected(charities.find(charity => charity._id === currentCharityId))
      } 
    }
  }, [charities])

  if (error) {
    return <div className="flex w-full flex-col items-center">
      {translations.texts.charities_retrieve_error.toString()}
      <div className="flex flex-row gap-4 mt-6">
        <OutlinedButton onClick={() => { logout() }} label={translations.texts.logout.toString()} />
        <FilledButton onClick={() => { setError(undefined); getCharities() }} label={translations.texts.retry.toString()} />
      </div>
    </div>
  }

  if (!charities || checkSingleCharityOnLogin(charities)) {
    return <Loader className="h-8 w-8" />
  }

  return <div className="flex w-full flex-col items-center">
    <CharitiesPicklist values={charities} value={selected} onChange={setSelected} />
    <div className="flex flex-row gap-4 mt-6">
      <OutlinedButton onClick={() => { logout() }} label={translations.texts.logout.toString()} />
      <FilledButton onClick={() => {
        if (selected) {
          loadCharity(selected._id)
        }
      }} label={translations.texts.confirm.toString()} loading={loading} disabled={!selected} />
    </div>
  </div>
}

function CharitySelectionItem({ name }: { name: string }): JSX.Element {
  return <div className="flex flex-row">{name}</div >
}